import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { computeDeviceLayout } from '../utils/deviceLayout';

/** @typedef {ReturnType<typeof computeDeviceLayout>} DeviceLayoutSnapshot */

const DeviceLayoutContext = createContext(
  /** @type {DeviceLayoutSnapshot | null} */(null)
);

function readSnapshot() {
  if (typeof window === 'undefined') {
    return computeDeviceLayout(1024, 768);
  }
  return computeDeviceLayout(window.innerWidth, window.innerHeight);
}

export function DeviceLayoutProvider({ children }) {
  const [snapshot, setSnapshot] = useState(() => readSnapshot());

  useEffect(() => {
    const update = () => {
      setSnapshot(computeDeviceLayout(window.innerWidth, window.innerHeight));
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);

    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', update);
    }

    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      if (vv) {
        vv.removeEventListener('resize', update);
      }
    };
  }, []);

  const {
    isMobileLayout,
    isLandscape,
    isDesktopLayout,
  } = snapshot;

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.mobileLayout = isMobileLayout ? 'true' : 'false';
    root.dataset.desktopLayout = isDesktopLayout ? 'true' : 'false';
    root.dataset.orientation = isLandscape ? 'landscape' : 'portrait';

    return () => {
      delete root.dataset.mobileLayout;
      delete root.dataset.desktopLayout;
      delete root.dataset.orientation;
    };
  }, [isMobileLayout, isDesktopLayout, isLandscape]);

  const value = useMemo(
    () => ({
      ...snapshot,
    }),
    [snapshot]
  );

  return (
    <DeviceLayoutContext.Provider value={value}>
      {children}
    </DeviceLayoutContext.Provider>
  );
}

/** @returns {DeviceLayoutSnapshot} */
export function useDeviceLayout() {
  const ctx = useContext(DeviceLayoutContext);
  if (!ctx) {
    throw new Error('useDeviceLayout must be used within a DeviceLayoutProvider');
  }
  return ctx;
}
